import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { buildFaturaFilename, type FaturaFilenameMeta } from '@/lib/faturaFilename';

/**
 * Hook para download de faturas do Storage.
 * Filename DESCRITIVO via buildFaturaFilename — princípio Douglas 24/05:
 * "PARA NAO GERAR NUNCA MAIS COMO BEE". Todo download de fatura sai com
 * nome RAZAO_CNPJ_VENC_Mes-Ano.pdf.
 *
 * Se o caller não passar metadata, cai num filename neutro "fatura.pdf"
 * (melhor que expor "1778855_fatura_bee360.pdf" do storage).
 */
async function resolverMetaPorArquivo(arquivoUrl: string, path: string): Promise<FaturaFilenameMeta | undefined> {
  try {
    const { data: fu } = await supabase
      .from('fatura_upload')
      .select('id, cliente_id, periodo_fim, created_at')
      .in('arquivo_url', Array.from(new Set([arquivoUrl, path, `faturas/${path}`])))
      .limit(1)
      .maybeSingle();
    if (!fu) return undefined;
    const [{ data: cli }, { data: conta }] = await Promise.all([
      fu.cliente_id
        ? supabase.from('clientes').select('razao_social, nome_fantasia, cnpj').eq('id', fu.cliente_id).maybeSingle()
        : Promise.resolve({ data: null }),
      supabase.from('base_fatura_conta').select('vencimento').eq('fatura_upload_id', fu.id).limit(1).maybeSingle(),
    ]);
    return {
      razao_social: cli?.razao_social,
      nome_fantasia: cli?.nome_fantasia,
      cnpj: cli?.cnpj,
      periodo_fim: conta?.vencimento || fu.periodo_fim,
      created_at: fu.created_at,
    };
  } catch {
    return undefined;
  }
}

export function useDownloadFatura() {
  const downloadFatura = async (arquivoUrl: string, meta?: FaturaFilenameMeta) => {
    if (!arquivoUrl) {
      console.warn('[DOWNLOAD] URL do arquivo está vazia');
      toast({
        title: '⚠️ Arquivo não disponível',
        description: 'A URL do arquivo não foi encontrada na solicitação',
        variant: 'destructive',
      });
      return;
    }

    try {
      // Remove prefixo 'faturas/' se existir (consistência de path)
      const path = arquivoUrl.replace(/^faturas\//, '');
      
      console.log('[DOWNLOAD] Tentando download:', {
        originalUrl: arquivoUrl,
        normalizedPath: path,
        bucket: 'faturas',
      });
      
      // Tentar download direto primeiro
      const { data, error } = await supabase.storage
        .from('faturas')
        .download(path);

      if (error) {
        // Verificar se é erro de "not found"
        const isNotFound = 
          error.message?.toLowerCase().includes('not found') ||
          error.message?.toLowerCase().includes('object not found') ||
          (error as any)?.statusCode === 404;

        if (isNotFound) {
          console.error('[DOWNLOAD] Arquivo não encontrado no Storage:', {
            path,
            bucket: 'faturas',
            error: error.message,
          });
          toast({
            title: '📁 Arquivo não encontrado',
            description: `O arquivo "${path}" não existe no Storage. Pode ter sido removido ou ainda não foi processado.`,
            variant: 'destructive',
          });
          return;
        }

        console.warn('[DOWNLOAD] Erro no download direto, tentando signed URL:', error);
        
        // Fallback: criar URL assinada (válida por 60 segundos)
        const { data: signedData, error: signedError } = await supabase.storage
          .from('faturas')
          .createSignedUrl(path, 60);
        
        if (signedError) {
          const isSignedNotFound = 
            signedError.message?.toLowerCase().includes('not found') ||
            (signedError as any)?.statusCode === 404;

          if (isSignedNotFound) {
            console.error('[DOWNLOAD] Arquivo não encontrado (signed URL):', {
              path,
              bucket: 'faturas',
              error: signedError.message,
            });
            toast({
              title: '📁 Arquivo não encontrado',
              description: `O arquivo não existe no Storage. Verifique se o processamento foi concluído.`,
              variant: 'destructive',
            });
            return;
          }

          console.error('[DOWNLOAD] Erro ao criar signed URL:', signedError);
          throw signedError;
        }
        
        // Abrir URL assinada em nova aba para download
        window.open(signedData.signedUrl, '_blank');
        
        toast({
          title: '✅ Download iniciado',
          description: 'O arquivo será baixado em uma nova aba',
        });
        return;
      }
      
      // Download direto funcionou - criar blob e baixar
      // Filename SEMPRE descritivo (RAZAO_CNPJ_VENC_Mes-Ano.pdf). Quando
      // o caller não tem metadata do cliente/fatura, cai em "fatura.pdf"
      // genérico — nunca expõe o nome cru do storage com "bee360".
      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      const metaFinal = meta ?? await resolverMetaPorArquivo(arquivoUrl, path);
      link.download = metaFinal ? buildFaturaFilename(metaFinal) : 'fatura.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast({
        title: '✅ Download iniciado',
        description: 'O arquivo está sendo baixado',
      });
    } catch (error: any) {
      console.error('[DOWNLOAD] Erro completo:', {
        message: error?.message,
        error: error?.error,
        statusCode: error?.statusCode,
        arquivoUrl,
      });

      // Mensagem mais amigável baseada no tipo de erro
      let descricao = 'Não foi possível baixar o arquivo. Tente novamente.';
      
      if (error?.message?.includes('network')) {
        descricao = 'Erro de conexão. Verifique sua internet e tente novamente.';
      } else if (error?.message?.includes('timeout')) {
        descricao = 'A operação demorou demais. Tente novamente em alguns instantes.';
      }

      toast({
        title: '❌ Erro ao baixar',
        description: descricao,
        variant: 'destructive',
      });
    }
  };

  return { downloadFatura };
}
